import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp, LoaderCircle, TriangleAlert, X } from 'lucide-react';
import './ConfirmationModal.scss';

const FOCUSABLE = [
    'button:not(:disabled)',
    'a[href]',
    'input:not(:disabled)',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    '[tabindex]',
    '[contenteditable="true"]'
].join(',');

function ConfirmationDialog({
    onCancel,
    onConfirm,
    title = 'Confirm action',
    children,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'default',
    pendingText = 'Confirming…',
    returnFocusRef
}) {
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const dialog = useRef(null);
    const cancelButton = useRef(null);
    const pendingRef = useRef(false);
    const mounted = useRef(false);
    const cancel = useRef(onCancel);
    const returnFocus = useRef(returnFocusRef);
    const titleId = useId();
    const descriptionId = useId();
    const isDanger = variant === 'danger';
    const Icon = isDanger ? TriangleAlert : CircleHelp;
    cancel.current = onCancel;
    returnFocus.current = returnFocusRef;

    useLayoutEffect(() => {
        mounted.current = true;
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        cancelButton.current?.focus();

        const focusableElements = () => [...dialog.current.querySelectorAll(FOCUSABLE)]
            .filter(element => {
                const style = window.getComputedStyle(element);
                return element.tabIndex >= 0
                    && !element.closest('[hidden], [inert], [aria-hidden="true"]')
                    && style.display !== 'none'
                    && style.visibility !== 'hidden';
            });

        const handleKeyDown = event => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                if (!pendingRef.current) cancel.current();
                return;
            }
            if (event.key !== 'Tab') return;
            const elements = focusableElements();
            const first = elements[0];
            const last = elements[elements.length - 1];
            if (!first) {
                event.preventDefault();
                dialog.current.focus();
            } else if (!dialog.current.contains(document.activeElement)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) {
                event.preventDefault();
                first.focus();
            }
        };

        const containFocus = event => {
            if (!dialog.current.contains(event.target)) {
                (pendingRef.current ? dialog.current : cancelButton.current)?.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('focusin', containFocus);
        return () => {
            mounted.current = false;
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('focusin', containFocus);
            document.body.style.overflow = previousOverflow;
            const canRestoreFocus = element => element?.isConnected
                && !element.matches(':disabled, [aria-disabled="true"]')
                && typeof element.focus === 'function';
            const canRestorePreviousFocus = canRestoreFocus(previousFocus);
            if (canRestorePreviousFocus) previousFocus.focus();
            if ((!canRestorePreviousFocus || document.activeElement !== previousFocus)
                && canRestoreFocus(returnFocus.current?.current)) {
                returnFocus.current.current.focus();
            }
        };
    }, []);

    useEffect(() => {
        if (pending) dialog.current?.focus();
        else if (error) cancelButton.current?.focus();
    }, [pending, error]);

    const dismiss = () => {
        if (!pendingRef.current) cancel.current();
    };

    const confirm = async () => {
        if (pendingRef.current) return;
        pendingRef.current = true;
        setPending(true);
        setError('');
        try {
            await onConfirm();
            if (mounted.current) cancel.current();
        } catch (confirmationError) {
            if (mounted.current) {
                setError(confirmationError?.message || 'This action could not be completed. Please try again.');
            }
        } finally {
            pendingRef.current = false;
            if (mounted.current) setPending(false);
        }
    };

    return (
        <div
            className="confirmation-modal-overlay"
            onClick={event => {
                if (event.target === event.currentTarget) dismiss();
            }}
        >
            <section
                ref={dialog}
                className={`confirmation-modal${isDanger ? ' confirmation-modal--danger' : ''}`}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                aria-busy={pending}
                tabIndex={-1}
            >
                <button
                    type="button"
                    className="confirmation-modal__close"
                    aria-label="Close confirmation"
                    disabled={pending}
                    onClick={dismiss}
                >
                    <X size={20} aria-hidden="true" />
                </button>
                <div className="confirmation-modal__icon">
                    <Icon size={24} aria-hidden="true" />
                </div>
                <h2 id={titleId} className="confirmation-modal__title">{title}</h2>
                <div id={descriptionId} className="confirmation-modal__message">
                    {children ?? message}
                </div>
                {error && (
                    <div className="confirmation-modal__error" role="alert">
                        <TriangleAlert size={17} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}
                <div className="confirmation-modal__actions">
                    <button
                        ref={cancelButton}
                        type="button"
                        className="confirmation-modal__button confirmation-modal__button--cancel"
                        disabled={pending}
                        onClick={dismiss}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className="confirmation-modal__button confirmation-modal__button--confirm"
                        disabled={pending}
                        onClick={confirm}
                    >
                        {pending && <LoaderCircle size={17} className="confirmation-modal__spinner" aria-hidden="true" />}
                        {pending ? pendingText : confirmText}
                    </button>
                </div>
            </section>
        </div>
    );
}

export default function ConfirmationModal({ isOpen, ...props }) {
    if (!isOpen) return null;
    return createPortal(<ConfirmationDialog {...props} />, document.body);
}
